import { BusyIndicator, Title } from "@ui5/webcomponents-react";
import type { ReactNode } from "react";

import "./floorplans.css";

export interface PageHeaderProps {
  /** Translated page title. */
  title: ReactNode;
  /** Short line under the title. */
  subtitle?: ReactNode;
  /** Page-level actions (Create, Export, …), already permission-filtered; shown at the end of the title row. */
  actions?: ReactNode;
  /** Test id of the title text (e.g. a record name a test reads). */
  titleTestId?: string;
  /** Extra class of the header (e.g. to hide it when printing). */
  className?: string;
  "data-testid"?: string;
}

/**
 * Title bar of every page that is not a List Report or Object Page (setup lists, runs, reports, settings): the title
 * at the start in the large Dynamic Page title style, an optional subtitle under it, and the page's actions at the end
 * of the same row, on the same white header band as the List Report floorplan (RV-S-08, RV-P-06, RV-C-07, RV-M-13).
 * Pages use it instead of their own `Title` + toolbar so every page header looks the same (convention test).
 */
export function PageHeader({
  title,
  subtitle,
  actions,
  titleTestId,
  className,
  "data-testid": testId,
}: PageHeaderProps) {
  return (
    <header
      className={className ? `positive-page-header ${className}` : "positive-page-header"}
      {...(testId ? { "data-testid": testId } : {})}
    >
      <div className="positive-page-header-heading">
        <Title
          level="H2"
          className="positive-page-header-title"
          {...(titleTestId ? { "data-testid": titleTestId } : {})}
        >
          {title}
        </Title>
        {subtitle !== undefined && subtitle !== null && <div className="positive-page-header-subtitle">{subtitle}</div>}
      </div>
      {actions !== undefined && actions !== null && actions !== false && (
        <div className="positive-page-header-actions">{actions}</div>
      )}
    </header>
  );
}

export interface PageLoadingProps {
  /** Title of the page being loaded, shown at once so the user knows where they are. */
  title?: ReactNode;
  "data-testid"?: string;
}

/** A page whose data is still loading: its title and a skeleton of the content, never an empty screen. */
export function PageLoading({ title, "data-testid": testId = "page-loading" }: PageLoadingProps) {
  return (
    <section className="positive-page-loading" aria-busy="true" data-testid={testId}>
      {title !== undefined && <PageHeader title={title} />}
      <div className="positive-page-skeleton" aria-hidden="true">
        {Array.from({ length: 6 }, (_, index) => (
          <span key={index} className="positive-page-skeleton-bar" />
        ))}
      </div>
      <BusyIndicator active delay={0} />
    </section>
  );
}
